import { apiClient } from './client';
import type {
  MachineryRecord,
  MachinerySession,
  MachinerySessionCostSummary,
  FieldActivityCatalogEntry,
  MachineryServiceCatalogEntry,
  FuelPriceRecord,
} from '../types/api.types';

// ---------------------------------------------------------------------------
// machinery.api.ts — Agrovid Dashboard
// GET  /api/machinery                      → lista maquinaria
// GET  /api/machinery/catalog/activities   → catálogo de actividades
// GET  /api/machinery/catalog/services     → catálogo de servicios
// GET  /api/machinery/fuel-price           → precio actual de combustible
// PUT  /api/machinery/fuel-price           → actualizar precio (ADMIN)
// GET  /api/machinery/sessions/recent      → últimas sesiones
// GET  /api/machinery/:id/sessions         → sesiones de una máquina
// ---------------------------------------------------------------------------

export type MachineryInput = {
  name:              string;
  type:              MachineryRecord['type'];
  model?:            string;
  year?:             number;
  plate?:            string;
  fuelType?:         'DIESEL' | 'GASOLINE' | 'ELECTRIC';
  isRental?:         boolean;
  rentalOwner?:      string;
  rentalCostPerDay?: number;
  ranchoId?:         string;
  // drone
  droneModel?:         string;
  serialNumber?:       string;
  tankCapacityL?:      number;
  coveragePerLoadHa?:  number;
  operatorName?:       string;
  operatorPhone?:      string;
  operatorLicense?:    string;
  licenseExpiry?:      string;
  registrationNumber?: string;
};

/** `ranchoId` solo filtra si el ajuste `machinery.scopeByRancho` está activo en el backend. */
export const fetchMachinery = async (ranchoId?: string | null): Promise<MachineryRecord[]> =>
  (await apiClient.get('/api/machinery', { params: ranchoId ? { ranchoId } : undefined })).data;

export const createMachinery = async (input: MachineryInput): Promise<MachineryRecord> =>
  (await apiClient.post('/api/machinery', input)).data;

export const updateMachinery = async (machineryId: string, input: Partial<MachineryInput> & { status?: MachineryRecord['status'] }): Promise<void> =>
  (await apiClient.put(`/api/machinery/${machineryId}`, input)).data;

export const deleteMachinery = async (machineryId: string): Promise<void> =>
  (await apiClient.delete(`/api/machinery/${machineryId}`)).data;

export const fetchActivityCatalog = async (): Promise<FieldActivityCatalogEntry[]> =>
  (await apiClient.get('/api/machinery/catalog/activities')).data;

export const fetchServiceCatalog = async (): Promise<MachineryServiceCatalogEntry[]> =>
  (await apiClient.get('/api/machinery/catalog/services')).data;

export const fetchCurrentFuelPrice = async (): Promise<FuelPriceRecord | null> =>
  (await apiClient.get('/api/machinery/fuel-price')).data;

export const setFuelPrice = async (input: {
  pricePerLiter:  number;
  effectiveFrom?: string;
  litersLoaded?:  number;
  notes?:         string;
}): Promise<FuelPriceRecord> =>
  (await apiClient.put('/api/machinery/fuel-price', input)).data;

export const fetchRecentSessions = async (limit = 20): Promise<MachinerySession[]> =>
  (await apiClient.get('/api/machinery/sessions/recent', { params: { limit } })).data;

export const fetchMachineSessions = async (machineryId: string, limit = 30): Promise<MachinerySession[]> =>
  (await apiClient.get(`/api/machinery/${machineryId}/sessions`, { params: { limit } })).data;

// Cronómetro sobre sesiones (mismo modelo/servicio que el bot)
export const fetchActiveSessions = async (): Promise<MachinerySession[]> =>
  (await apiClient.get('/api/machinery/sessions/active')).data;

export const startMachinerySession = async (input: {
  machineryId: string; loteId: string; moduleId?: string; activity: string;
}): Promise<MachinerySession> =>
  (await apiClient.post('/api/machinery/sessions/start', input)).data;

/** Registra el costo de una sesión cerrada en contabilidad; solo tras el clic explícito del usuario. */
export const confirmMachinerySessionCost = async (sessionId: string, input: {
  hectaresCompleted: number; budgetCapPerHa?: number; rentalCostPerDay?: number; laborCost?: number;
}): Promise<{ alreadyRegistered: boolean; budgetWarning?: string }> =>
  (await apiClient.post(`/api/machinery/sessions/${sessionId}/confirm-cost`, input)).data;

export const stopMachinerySession =async (sessionId: string, input: {
  fuelLiters?: number; fuelCostPerLiter?: number; hourlyRate?: number; notes?: string;
  hectaresCompleted?: number; budgetCapPerHa?: number; rentalCostPerDay?: number;
}): Promise<MachinerySession & { costSummary?: MachinerySessionCostSummary }> =>
  (await apiClient.post(`/api/machinery/sessions/${sessionId}/stop`, input)).data;
